import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { SPEI_TYPE, isSpeiEngineNode, speiEngineConfig } from './flowSpei';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Panel,
  applyNodeChanges,
  applyEdgeChanges,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { authService } from '../../../services/api';
import { useNotification } from '../../../components/Notifications/NotificationContext';
import { validateFields } from '../../../utils/contentBlacklistValidator';

import FlowNodeComponent from './components/FlowNode';
import BotChip from '../../../components/BotChip/BotChip';
import { useBotPhotos } from '../../../hooks/useBotPhotos';
import { useAuth } from '../../../context/AuthProvider';
import { getVisibleBots } from '../../../utils/bots';
import { FlowStatsContext } from './FlowStatsContext';
import { NODE_COLORS, BLOCK_GROUPS } from './flowConstants';
import DeletableEdge from './components/DeletableEdge';
import FlowSidebar from './components/FlowSidebar';
import FlowMarketSettings from './components/FlowMarketSettings';
import './FlowLoader.css';
import {
  fromDelaySeconds,
  fromTypingSeconds,
  getSmartDelayDisplay,
  toDelaySeconds,
  toSmartDelaySeconds,
  toTypingSeconds,
} from './flowUtils';

const DEFAULT_FLOW_ID = 'start';

// Tela de loading do editor. Como overlay, cobre o canvas enquanto o ReactFlow
// monta/centraliza os nós e some com fade — evita o "flash" de canvas vazio.
function FlowLoaderScreen({ overlay = false, hiding = false }) {
  return (
    <div
      className={`flow-loader${overlay ? ' flow-loader--overlay' : ''}${hiding ? ' flow-loader--hide' : ''}`}
    >
      <div className="flow-loader__graph">
        <div className="flow-loader__node">
          <span className="flow-loader__node-icon" />
          <span className="flow-loader__node-lines"><span /><span /></span>
        </div>
        <div className="flow-loader__link" />
        <div className="flow-loader__node">
          <span className="flow-loader__node-icon" />
          <span className="flow-loader__node-lines"><span /><span /></span>
        </div>
        <div className="flow-loader__link" />
        <div className="flow-loader__node">
          <span className="flow-loader__node-icon" />
          <span className="flow-loader__node-lines"><span /><span /></span>
        </div>
      </div>
      <div className="flow-loader__text">Carregando fluxo</div>
    </div>
  );
}

// type do nó → { label, icon } (para a barra de navegação inferior)
const NODE_META = (() => {
  const map = { start: { label: 'Início', icon: 'fas fa-play' } };
  BLOCK_GROUPS.forEach((g) => g.items.forEach((it) => { map[it.type] = { label: it.label, icon: it.icon }; }));
  return map;
})();

// Resolve os ramos de saída de um nó (a partir das conexões) → [{ targetId, label, tone }]
function resolveBranches(node, outgoingEdges) {
  const data = node?.data || {};
  return outgoingEdges.map((e) => {
    const h = e.sourceHandle || '';
    let label = 'Próximo';
    let tone = 'default';
    if (h === 'source-paid') { label = 'Pago'; tone = 'green'; }
    else if (h === 'source-unpaid') { label = 'Não pago'; tone = 'red'; }
    else if (h === 'source-ok') { label = 'OK'; tone = 'green'; }
    else if (h === 'source-timeout') { label = 'Timeout'; tone = 'red'; }
    else if (h === 'source-accepted') { label = 'Aceitou'; tone = 'green'; }
    else if (h === 'source-declined') { label = 'Recusou'; tone = 'red'; }
    else if (h && h.startsWith('source-')) {
      const rawId = h.slice('source-'.length);
      const btn = (data.buttonItems || []).find((b) => b.id === rawId);
      const out = (data.randomizerOutputs || []).find((o) => o.id === rawId);
      if (btn) label = btn.text || 'Botão';
      else if (out) label = `${out.percent}%`;
    }
    return { targetId: e.target, label, tone };
  });
}

// Junta os textos pesquisáveis de um nó (para a busca do editor)
function nodeSearchText(node) {
  const d = node?.data || {};
  const parts = [
    d.text, d.entregavelText, d.entregavelButtonText, d.messageBefore, d.messageAfter,
    d.orderBumpMessage, d.inputPrompt, d.planBeforePixMessage, d.paymentInstructionsText,
    d.nodeType,
  ];
  if (Array.isArray(d.buttonItems)) parts.push(...d.buttonItems.map((b) => b?.text));
  return parts.filter(Boolean).join(' \n ').toLowerCase();
}

function definitionToFlow(definition) {
  if (!definition || typeof definition !== 'object') {
    // Nenhum fluxo salvo ainda: canvas começa vazio e criaremos START depois
    return { nodes: [], edges: [] };
  }

  const nodesCfg = definition.nodes || {};

  let nodes = Object.values(nodesCfg)
    .filter((node) => {
      // Não renderizar sentinelas de start nem nós filhos de composed (marcados com _composed_parent).
      return node.type !== 'start' && !node.config?._composed_parent;
    })
    .map((node) => {
      const config = node.config || {};
      const media = config.media || {};
      let visualType = node.type;
      const showTyping = Boolean(config.show_typing ?? config.showTyping);
      let delayValue = '';
      let delayUnit = 'seconds';
      let smartDelayMin = '';
      let smartDelayMax = '';
      let smartDelayUnit = 'seconds';
      let typingValue = '';
      let typingUnit = 'seconds';
      let typingAction = 'typing';
      let paymentPlanName = '';
      let paymentPlanDuration = 'vitalicio';
      let paymentAmount = '';
      let cardMessage = '';
      let cardButtonText = '';
      let pixCodeFormat = 'blockquote';
      let messageBefore = '';
      let messageAfter = '';
      let showQrButton = true;
      let showCopyButton = true;
      let showVerifyButton = true;
      let verifyButtonText = '✅Verificar Pagamento';
      let copyButtonText = '📋 Copiar Código';
      let qrButtonText = '📱Ver QR Code';
      let pendingTimeoutMinutes = 30;
      let showPlanBeforePix = false;
      let planBeforePixMessage = '';
      let planBeforePixButtonText = '';
      let showPaymentInstructions = true;
      let paymentInstructionsText = '';

      // Input node
      let inputPrompt = '';
      let inputVar = '';
      let inputType = 'text';
      let inputTimeout = 60;

      // Randomizer node
      let randomizerOutputs = [
        { id: 'r0', percent: 50 },
        { id: 'r1', percent: 50 },
      ];

      // Trigger node
      let triggerType = 'message';
      let triggerTimeoutValue = 60;
      let triggerTimeoutUnit = 'seconds';

      // Order Bump node
      let orderBumpName = '';
      let orderBumpPrice = '';

      let orderBumpMessage = '';
      let orderBumpMedia = [];
      let orderBumpGrouped = false;
      let orderBumpHideDecline = false;
      let orderBumpDisappearOnClick = false;
      let orderBumpAcceptColor = '';
      let orderBumpDeclineColor = '';
      let orderBumpAcceptLabel = '';
      let orderBumpDeclineLabel = '';

      if (node.type === 'message') {
        const kind = config.kind || 'text';
        if (['text', 'image', 'video', 'audio'].includes(kind)) {
          visualType = kind;
        } else {
          visualType = 'text';
        }
      }

      if (node.type === 'delay') {
        const mode = (config.mode || 'fixed').toLowerCase();
        if (mode === 'smart' || config.min != null || config.max != null) {
          const display = getSmartDelayDisplay(config.min, config.max);
          visualType = 'smart_delay';
          smartDelayMin = display.minValue;
          smartDelayMax = display.maxValue;
          smartDelayUnit = display.unit;
        } else {
          const parsed = fromDelaySeconds(config.seconds);
          delayValue = parsed.value;
          delayUnit = parsed.unit;
        }
      }

      if (node.type === 'typing') {
        const parsed = fromTypingSeconds(config.seconds);
        typingValue = parsed.value;
        typingUnit = parsed.unit;
        typingAction = config.action || config.typing_action || 'typing';
      }

      if (node.type === 'input') {
        inputPrompt = config.prompt || '';
        inputVar = config.var || '';
        inputType = config.type || 'text';
        inputTimeout = config.timeout_seconds ?? 60;
      }

      if (node.type === 'randomizer') {
        visualType = 'randomizer';
        randomizerOutputs = config.outputs || [
          { id: 'r0', percent: 50 },
          { id: 'r1', percent: 50 },
        ];
      }

      if (node.type === 'trigger') {
        visualType = 'trigger';
        triggerType = config.trigger_type || 'message';
        const rawSec = config.timeout_seconds ?? 60;
        if (rawSec % 3600 === 0 && rawSec >= 3600) {
          triggerTimeoutValue = rawSec / 3600;
          triggerTimeoutUnit = 'hours';
        } else if (rawSec % 60 === 0 && rawSec >= 60) {
          triggerTimeoutValue = rawSec / 60;
          triggerTimeoutUnit = 'minutes';
        } else {
          triggerTimeoutValue = rawSec;
          triggerTimeoutUnit = 'seconds';
        }
      }

      // Buttons node
      let buttonItems = [{ id: 'btn_0', text: 'Botão 1', color: 'default' }, { id: 'btn_1', text: 'Botão 2', color: 'default' }];
      let buttonDeleteAfterClick = false;

      // Composed node
      let composedElements = [];

      // Goto node
      let gotoTargetId = '';

      let entregavelDeliveryType = 'text';
      let entregavelText = '';
      let entregavelUrl = '';
      let entregavelButtonText = '';
      let entregavelVipGroupId = '';

      if (node.type === 'button') {
        visualType = 'buttons';
        buttonItems = (config.buttons || []).map((b) => ({ id: b.id || b.text, text: b.text || '', color: b.color || 'default' }));
        if (buttonItems.length === 0) buttonItems = [{ id: 'btn_0', text: '' }, { id: 'btn_1', text: '' }];
        buttonDeleteAfterClick = Boolean(config.delete_after_click);
      }

      if (node.type === 'composed') {
        // Novo formato: reconstrói composedElements a partir dos nós filhos reais
        const elementIds = config.element_ids || [];
        if (elementIds.length > 0) {
          composedElements = elementIds.map((elId) => {
            const elNode = nodesCfg[elId];
            if (!elNode) return null;
            const elType = elNode.config?._el_type || elNode.type;
            const elCfg = elNode.config || {};
            if (elType === 'text') {
              return { type: 'text', elId, config: { text: elCfg.text || '' } };
            } else if (elType === 'image') {
              return { type: 'image', elId, config: { media: elCfg.media || {}, caption: elCfg.caption || '' } };
            } else if (elType === 'video') {
              return { type: 'video', elId, config: { media: elCfg.media || {}, caption: elCfg.caption || '' } };
            } else if (elType === 'audio') {
              return { type: 'audio', elId, config: { media: elCfg.media || {} } };
            } else if (elType === 'delay') {
              return { type: 'delay', elId, config: { mode: elCfg.mode || 'fixed', value: elCfg.value, unit: elCfg.unit || 'seconds', seconds: elCfg.seconds || 3, show_typing: Boolean(elCfg.show_typing) } };
            } else if (elType === 'typing') {
              return { type: 'typing', elId, config: { action: elCfg.action || 'typing', value: elCfg.value, unit: elCfg.unit || 'seconds', seconds: elCfg.seconds || 2 } };
            } else if (elType === 'location') {
              return { type: 'location', elId, config: {} };
            }
            return null;
          }).filter(Boolean);
        } else {
          // Legado: elementos inline
          composedElements = config.elements || [];
        }
      }

      if (node.type === 'goto') {
        gotoTargetId = config.target_node_id || '';
      }

      if (node.type === 'entregavel') {
        entregavelDeliveryType = config.delivery_type || 'vip_link';
        entregavelText = config.text || '';
        entregavelUrl = config.url || '';
        entregavelButtonText = config.button_text || '';
        entregavelVipGroupId = config.vip_group_id || '';
      }

      if (node.type === 'order_bump') {
        orderBumpName = config.name || '';
        orderBumpPrice = config.price ?? '';

        orderBumpMessage = config.message || '';
        orderBumpMedia = config.media || [];
        orderBumpGrouped = Boolean(config.grouped);
        orderBumpHideDecline = Boolean(config.hide_decline);
        orderBumpDisappearOnClick = Boolean(config.disappear_on_click);
        orderBumpAcceptColor = config.accept_color || '';
        orderBumpDeclineColor = config.decline_color || '';
        orderBumpAcceptLabel = config.accept_label || '';
        orderBumpDeclineLabel = config.decline_label || '';
      }

      if (node.type === 'payment') {
        const planInfo = config.plan_info || {};
        const pixConfig = config.pix_config || {};
        paymentAmount = config.amount ?? '';
        paymentPlanName = planInfo.name || '';
        paymentPlanDuration = planInfo.duration || 'vitalicio';
        pixCodeFormat = pixConfig.code_format || 'blockquote';
        messageBefore = pixConfig.message_before || '';
        messageAfter = pixConfig.message_after || '';
        showQrButton = pixConfig.show_qr_button !== false;
        showCopyButton = pixConfig.show_copy_button !== false;
        showVerifyButton = pixConfig.show_verify_button !== false;
        verifyButtonText = pixConfig.verify_button_text || '✅Verificar Pagamento';
        copyButtonText = pixConfig.copy_button_text || '📋 Copiar Código';
        qrButtonText = pixConfig.qr_button_text || '📱Ver QR Code';
        pendingTimeoutMinutes = pixConfig.pending_timeout_minutes ?? 30;
        showPlanBeforePix = Boolean(pixConfig.show_plan_before_pix);
        planBeforePixMessage = pixConfig.plan_before_pix_message || '';
        planBeforePixButtonText = pixConfig.plan_before_pix_button_text || '';
        showPaymentInstructions = pixConfig.show_payment_instructions !== false;
        paymentInstructionsText = pixConfig.payment_instructions_text || '';
      }

      if (node.type === 'card_payment') {
        const planInfo = config.plan_info || {};
        const pixConfig = config.pix_config || {};
        paymentAmount = config.amount ?? '';
        paymentPlanName = planInfo.name || '';
        paymentPlanDuration = planInfo.duration || 'mensal';
        cardMessage = config.card_message || '';
        cardButtonText = config.card_button_text || '';
        pendingTimeoutMinutes = pixConfig.pending_timeout_minutes ?? 30;
        // SPEI é um card_payment com method='spei' no motor; no editor, nó próprio.
        if (isSpeiEngineNode(node)) visualType = SPEI_TYPE;
      }

      // O nó START visual é sempre criado abaixo se não existir.
      // Nós reais nunca têm isStart=true; aquela flag é só para o visual.
      return {
        id: node.id,
        type: 'flowNode',
        data: {
          id: node.id,
          nodeType: visualType,
          text: config.text || '',
          isStart: false,
          hasMedia: Boolean(media.photo || media.video || media.audio || media.voice),
          media,
          delayValue,
          delayUnit,
          showTyping,
          smartDelayMin,
          smartDelayMax,
          smartDelayUnit,
          typingValue,
          typingUnit,
          typingAction,
          paymentPlanName,
          paymentPlanDuration,
          paymentAmount,
          cardMessage,
          cardButtonText,
          pixCodeFormat,
          messageBefore,
          messageAfter,
          showQrButton,
          showCopyButton,
          showVerifyButton,
          verifyButtonText,
          copyButtonText,
          qrButtonText,
          pendingTimeoutMinutes,
          showPlanBeforePix,
          planBeforePixMessage,
          planBeforePixButtonText,
          showPaymentInstructions,
          paymentInstructionsText,
          inputPrompt,
          inputVar,
          inputType,
          inputTimeout,
          randomizerOutputs,
          triggerType,
          triggerTimeoutValue,
          triggerTimeoutUnit,
          entregavelDeliveryType,
          entregavelText,
          entregavelUrl,
          entregavelButtonText,
          entregavelVipGroupId,
          buttonItems,
          buttonDeleteAfterClick,
          composedElements,
          gotoTargetId,
          orderBumpName,
          orderBumpPrice,

          orderBumpMessage,
          orderBumpMedia,
          orderBumpGrouped,
          orderBumpHideDecline,
          orderBumpDisappearOnClick,
          orderBumpAcceptColor,
          orderBumpDeclineColor,
          orderBumpAcceptLabel,
          orderBumpDeclineLabel,
        },
        position: node.position || { x: Math.random() * 400, y: Math.random() * 200 },
      };
    });

  // Garante que exista sempre um bloco START visual
  const hasStart = nodes.some((n) => n.data?.nodeType === 'start');
  if (!hasStart) {
    nodes = [
      {
        id: 'start',
        type: 'flowNode',
        data: {
          id: 'start',
          nodeType: 'start',
          text: 'Quando o usuário inicia',
          isStart: true,
          hasMedia: false,
        },
        position: { x: 0, y: 0 },
      },
      ...nodes,
    ];
  }

  const edges = [];

  // Recria edge visual de START para o start_node (se houver)
  // Isso garante que ao carregar a definição, a conexão visual seja restabelecida
  if (definition.start_node) {
    edges.push({
      id: `e-start-${definition.start_node}`,
      source: 'start',
      target: definition.start_node,
      type: 'deletable',
    });
  }

  // Recria edges entre nós reais baseado em seus campos 'next' / 'unpaid_next' / 'timeout_next'
  Object.values(nodesCfg).forEach((node) => {
    if (node.next && node.type !== 'randomizer') {
      // Para composed: node.next aponta para o primeiro elemento (roteamento interno do engine).
      // A edge visual deve apontar para o próximo nó visual real = next do último elemento.
      // Isso evita que saves subsequentes leiam composedOriginalNext = el1 e criem loop infinito.
      let edgeTarget = node.next;
      if (node.type === 'composed') {
        const elementIds = node.config?.element_ids || [];
        if (elementIds.length > 0) {
          const lastElId = elementIds[elementIds.length - 1];
          const lastEl = nodesCfg[lastElId];
          const candidate = (lastEl && lastEl.next) ? lastEl.next : null;
          // Verifica se candidate é um nó visual real (não um elemento filho de composed)
          edgeTarget = (candidate && !nodesCfg[candidate]?.config?._composed_parent)
            ? candidate
            : null;
        }
      }
      if (edgeTarget) {
        edges.push({
          id: `e-${node.id}-${edgeTarget}`,
          source: node.id,
          target: edgeTarget,
          sourceHandle:
            node.type === 'payment' || node.type === 'card_payment'
              ? 'source-paid'
              : node.type === 'input' || node.type === 'trigger'
                ? 'source-ok'
                : node.type === 'order_bump'
                  ? 'source-accepted'
                  : undefined,
          type: 'deletable',
        });
      }
    }
    if ((node.type === 'payment' || node.type === 'card_payment') && node.unpaid_next) {
      edges.push({
        id: `e-${node.id}-${node.unpaid_next}-unpaid`,
        source: node.id,
        target: node.unpaid_next,
        sourceHandle: 'source-unpaid',
        type: 'deletable',
      });
    }
    if (node.type === 'input' && node.timeout_next) {
      edges.push({
        id: `e-${node.id}-${node.timeout_next}-timeout`,
        source: node.id,
        target: node.timeout_next,
        sourceHandle: 'source-timeout',
        type: 'deletable',
      });
    }
    if (node.type === 'trigger' && node.timeout_next) {
      edges.push({
        id: `e-${node.id}-${node.timeout_next}-timeout`,
        source: node.id,
        target: node.timeout_next,
        sourceHandle: 'source-timeout',
        type: 'deletable',
      });
    }
    if (node.type === 'order_bump' && node.declined_next) {
      edges.push({
        id: `e-${node.id}-${node.declined_next}-declined`,
        source: node.id,
        target: node.declined_next,
        sourceHandle: 'source-declined',
        type: 'deletable',
      });
    }
    if (node.type === 'randomizer') {
      (node.config?.outputs || []).forEach((output) => {
        if (output.next) {
          edges.push({
            id: `e-${node.id}-${output.next}-${output.id}`,
            source: node.id,
            target: output.next,
            sourceHandle: `source-${output.id}`,
            type: 'deletable',
          });
        }
      });
    }
    if (node.type === 'button') {
      (node.config?.buttons || []).forEach((btn) => {
        if (btn.next) {
          edges.push({
            id: `e-${node.id}-${btn.next}-${btn.id}`,
            source: node.id,
            target: btn.next,
            sourceHandle: `source-${btn.id}`,
            type: 'deletable',
          });
        }
      });
    }
  });

  return { nodes, edges };
}

function flowToDefinition(nodes, edges, prevDefinition) {
  const def = {
    ...(prevDefinition || {}),
  };

  const nodesMap = { ...(def.nodes || {}) };

  // Primeiro, identifica o alvo do bloco START visual (se existir)
  const startEdge = edges.find((e) => e.source === 'start');
  const startTarget = startEdge?.target || null;

  nodes.forEach((n) => {
    const visualType = n.data?.nodeType || 'text';
    const isVisualStart = visualType === 'start' || n.data?.isStart;

    // Nós START são apenas visuais: não viram nós reais no engine.
    if (isVisualStart) {
      return;
    }

    const engineType = ['text', 'image', 'video', 'audio'].includes(visualType)
      ? 'message'
      : visualType === 'smart_delay'
        ? 'delay'
        : visualType === 'buttons'
          ? 'button'
          : visualType === SPEI_TYPE
            ? 'card_payment'
            : visualType;

    const prev = nodesMap[n.id] || {
      id: n.id,
      type: engineType,
      next: null,
      config: {},
    };

    const config = { ...(prev.config || {}) };

    if (engineType === 'message') {
      config.kind = visualType;
      config.text = n.data?.text || '';
      config.media = n.data?.media || {};
    }

    if (engineType === 'delay') {
      if (visualType === 'smart_delay') {
        config.mode = 'smart';
        let minSeconds = toSmartDelaySeconds(n.data?.smartDelayMin, n.data?.smartDelayUnit);
        let maxSeconds = toSmartDelaySeconds(n.data?.smartDelayMax, n.data?.smartDelayUnit);
        if (maxSeconds > 0 && minSeconds > 0 && maxSeconds < minSeconds) {
          const tmp = minSeconds;
          minSeconds = maxSeconds;
          maxSeconds = tmp;
        }
        config.min = minSeconds;
        config.max = maxSeconds;
        config.show_typing = Boolean(n.data?.showTyping);
        delete config.seconds;
      } else {
        config.mode = 'fixed';
        config.seconds = toDelaySeconds(n.data?.delayValue, n.data?.delayUnit);
        config.show_typing = Boolean(n.data?.showTyping);
        delete config.min;
        delete config.max;
      }
    }

    if (engineType === 'payment') {
      config.amount = parseFloat(n.data?.paymentAmount) || 0;
      config.plan_info = {
        name: n.data?.paymentPlanName || '',
        duration: n.data?.paymentPlanDuration || 'vitalicio',
      };
      config.pix_config = {
        code_format: n.data?.pixCodeFormat || 'blockquote',
        message_before: n.data?.messageBefore || '',
        message_after: n.data?.messageAfter || '',
        show_qr_button: n.data?.showQrButton !== false,
        show_copy_button: n.data?.showCopyButton !== false,
        show_verify_button: n.data?.showVerifyButton !== false,
        verify_button_text: n.data?.verifyButtonText || '✅Verificar Pagamento',
        copy_button_text: n.data?.copyButtonText || '📋 Copiar Código',
        qr_button_text: n.data?.qrButtonText || '📱Ver QR Code',
        pending_timeout_minutes: parseInt(n.data?.pendingTimeoutMinutes, 10) || 30,
        show_plan_before_pix: Boolean(n.data?.showPlanBeforePix),
        plan_before_pix_message: n.data?.planBeforePixMessage || '',
        plan_before_pix_button_text: n.data?.planBeforePixButtonText || '',
        show_payment_instructions: n.data?.showPaymentInstructions !== false,
        payment_instructions_text: n.data?.paymentInstructionsText || '',
      };
    }

    if (visualType === SPEI_TYPE) {
      Object.assign(config, speiEngineConfig(n.data));
    } else if (engineType === 'card_payment') {
      config.amount = parseFloat(n.data?.paymentAmount) || 0;
      // Moeda-base vem do MODO do fluxo (top bar): BRL (padrão) ou USD. Não é
      // hardcode — segue o toggle do fluxo. Conversão p/ EUR/GBP fica na seleção.
      config.currency = def.currency_mode === 'usd' ? 'USD' : 'BRL';
      config.plan_info = {
        name: n.data?.paymentPlanName || '',
        duration: n.data?.paymentPlanDuration || 'mensal',
      };
      config.card_message = n.data?.cardMessage || '';
      config.card_button_text = n.data?.cardButtonText || '';
      config.pix_config = {
        pending_timeout_minutes: parseInt(n.data?.pendingTimeoutMinutes, 10) || 30,
      };
    }

    if (engineType === 'typing') {
      config.seconds = toTypingSeconds(n.data?.typingValue, n.data?.typingUnit);
      config.action = n.data?.typingAction || 'typing';
    }

    if (engineType === 'input') {
      config.prompt = n.data?.inputPrompt || '';
      config.var = n.data?.inputVar || '';
      config.type = n.data?.inputType || 'text';
      config.timeout_seconds = Math.max(5, parseInt(n.data?.inputTimeout, 10) || 60);
      config.validator = n.data?.inputType || 'text';
    }

    if (engineType === 'randomizer') {
      config.outputs = (n.data?.randomizerOutputs || [
        { id: 'r0', percent: 50 },
        { id: 'r1', percent: 50 },
      ]).map((o) => ({ ...o, next: o.next || null }));
    }

    if (engineType === 'trigger') {
      const tUnit = n.data?.triggerTimeoutUnit || 'seconds';
      const tVal = Math.max(1, parseInt(n.data?.triggerTimeoutValue, 10) || 1);
      const factors = { seconds: 1, minutes: 60, hours: 3600 };
      config.trigger_type = n.data?.triggerType || 'message';
      config.timeout_seconds = tVal * (factors[tUnit] || 1);
    }

    if (engineType === 'entregavel') {
      config.delivery_type = n.data?.entregavelDeliveryType || 'text';
      config.text = n.data?.entregavelText || '';
      if (config.delivery_type === 'external_link') {
        config.url = n.data?.entregavelUrl || '';
        config.button_text = n.data?.entregavelButtonText || '';
      } else if (config.delivery_type === 'vip_link') {
        config.vip_group_id = n.data?.entregavelVipGroupId || '';
        config.button_text = n.data?.entregavelButtonText || '';
      }
    }

    if (engineType === 'button') {
      const items = n.data?.buttonItems || [];
      config.text = n.data?.text || '';
      config.delete_after_click = Boolean(n.data?.buttonDeleteAfterClick);
      // next de cada botão é preenchido pelas edges abaixo
      config.buttons = items.map((btn) => ({
        id: btn.id,
        text: btn.text || '',
        color: btn.color || 'default',
        next: config.buttons?.find((b) => b.id === btn.id)?.next || null,
      }));
    }

    if (engineType === 'composed') {
      const elements = n.data?.composedElements || [];
      config.element_ids = elements.map((el) => el.elId).filter(Boolean);
      // Os nós filhos reais são injetados em nodesMap após o resolvedor de edges abaixo.
    }

    if (engineType === 'goto') {
      config.target_node_id = n.data?.gotoTargetId || null;
    }

    if (engineType === 'order_bump') {
      config.name = n.data?.orderBumpName || '';
      config.price = parseFloat(n.data?.orderBumpPrice) || 0;

      config.message = n.data?.orderBumpMessage || '';
      config.media = n.data?.orderBumpMedia || [];
      config.grouped = Boolean(n.data?.orderBumpGrouped);
      config.hide_decline = Boolean(n.data?.orderBumpHideDecline);
      config.disappear_on_click = Boolean(n.data?.orderBumpDisappearOnClick);
      config.accept_color = n.data?.orderBumpAcceptColor || '';
      config.decline_color = n.data?.orderBumpDeclineColor || '';
      config.accept_label = n.data?.orderBumpAcceptLabel || '';
      config.decline_label = n.data?.orderBumpDeclineLabel || '';
    }

    if (engineType === 'location') {
      // Sem config extra necessária
    }

    nodesMap[n.id] = {
      ...prev,
      type: engineType,
      config,
      position: n.position,
    };
  });

  // Remove nós que não existem mais no canvas
  const validIds = new Set(
    nodes
      .filter((n) => {
        const vt = n.data?.nodeType || 'text';
        return vt !== 'start' && !n.data?.isStart;
      })
      .map((n) => n.id),
  );
  Object.keys(nodesMap).forEach((id) => {
    if (!validIds.has(id)) {
      delete nodesMap[id];
    }
  });

  // Zera os next antes de recalcular a partir das edges
  Object.keys(nodesMap).forEach((id) => {
    nodesMap[id].next = null;
    nodesMap[id].unpaid_next = null;
    nodesMap[id].timeout_next = null;
    nodesMap[id].declined_next = null;
    if (nodesMap[id].type === 'randomizer') {
      (nodesMap[id].config?.outputs || []).forEach((o) => { o.next = null; });
    }
    if (nodesMap[id].type === 'button') {
      (nodesMap[id].config?.buttons || []).forEach((b) => { b.next = null; });
    }
  });

  // Reconstrói as conexões a partir das edges do canvas, excluindo aquelas que saem de START
  edges.forEach((e) => {
    if (e.source === 'start' || !nodesMap[e.source]) return;
    // Ignora edges que apontam para nós não-visuais (ex: nós filhos de composed).
    // Isso impede que edges obsoletas corrompam composedOriginalNext e criem loop infinito.
    if (!nodesMap[e.target]) return;

    const sourceNode = nodesMap[e.source];
    if (sourceNode.type === 'payment' || sourceNode.type === 'card_payment') {
      if (e.sourceHandle === 'source-unpaid') {
        sourceNode.unpaid_next = e.target;
      } else {
        sourceNode.next = e.target;
      }
    } else if (sourceNode.type === 'input') {
      if (e.sourceHandle === 'source-timeout') {
        sourceNode.timeout_next = e.target;
      } else {
        sourceNode.next = e.target;
      }
    } else if (sourceNode.type === 'trigger') {
      if (e.sourceHandle === 'source-timeout') {
        sourceNode.timeout_next = e.target;
      } else {
        sourceNode.next = e.target;
      }
    } else if (sourceNode.type === 'order_bump') {
      if (e.sourceHandle === 'source-declined') {
        sourceNode.declined_next = e.target;
      } else {
        sourceNode.next = e.target;
      }
    } else if (sourceNode.type === 'randomizer') {
      const outputId = e.sourceHandle?.replace('source-', '');
      const output = (sourceNode.config?.outputs || []).find((o) => o.id === outputId);
      if (output) output.next = e.target;
    } else if (sourceNode.type === 'button') {
      const btnId = e.sourceHandle?.replace('source-', '');
      const btn = (sourceNode.config?.buttons || []).find((b) => b.id === btnId);
      if (btn) btn.next = e.target;
    } else {
      sourceNode.next = e.target;
    }
  });

  // Define start_node com base na conexão a partir do nó START visual, se existir
  if (startTarget && nodesMap[startTarget]) {
    def.start_node = startTarget;
  } else if (!def.start_node || !nodesMap[def.start_node]) {
    const firstId = Object.keys(nodesMap)[0];
    if (firstId) {
      def.start_node = firstId;
    }
  }

  // Injeta nós reais para cada elemento de composed.
  // Cada elemento vira um nó próprio (message/delay/typing/location) com next encadeado.
  // Isso garante: (1) attach_media encontra o nó pelo elId, (2) o engine salva
  // current_node entre elementos → fluxo resumível após restart.
  nodes.forEach((n) => {
    if ((n.data?.nodeType || 'text') !== 'composed') return;
    const composedId = n.id;
    const composedNode = nodesMap[composedId];
    if (!composedNode) return;

    const composedOriginalNext = composedNode.next; // destino após o último elemento
    const elements = n.data?.composedElements || [];

    elements.forEach((el, i) => {
      const elId = el.elId;
      if (!elId) return;
      const cfg = el.config || {};
      const nextElId = i < elements.length - 1
        ? (elements[i + 1].elId || null)
        : composedOriginalNext;

      let elNodeType, elNodeConfig;
      if (el.type === 'text') {
        elNodeType = 'message';
        elNodeConfig = { kind: 'text', text: cfg.text || '', _composed_parent: composedId, _el_type: 'text' };
      } else if (el.type === 'image') {
        elNodeType = 'message';
        elNodeConfig = { kind: 'image', media: cfg.media || {}, caption: cfg.caption || '', _composed_parent: composedId, _el_type: 'image' };
      } else if (el.type === 'video') {
        elNodeType = 'message';
        elNodeConfig = { kind: 'video', media: cfg.media || {}, caption: cfg.caption || '', _composed_parent: composedId, _el_type: 'video' };
      } else if (el.type === 'audio') {
        elNodeType = 'message';
        elNodeConfig = { kind: 'audio', media: cfg.media || {}, _composed_parent: composedId, _el_type: 'audio' };
      } else if (el.type === 'delay') {
        elNodeType = 'delay';
        elNodeConfig = { mode: 'fixed', seconds: cfg.seconds || 3, show_typing: Boolean(cfg.show_typing), _composed_parent: composedId, _el_type: 'delay' };
      } else if (el.type === 'typing') {
        elNodeType = 'typing';
        elNodeConfig = { action: cfg.action || 'typing', seconds: cfg.seconds || 2, _composed_parent: composedId, _el_type: 'typing' };
      } else if (el.type === 'location') {
        elNodeType = 'location';
        elNodeConfig = { _composed_parent: composedId, _el_type: 'location' };
      } else {
        return;
      }

      // Preserva campos do nó existente (ex: mídia já anexada via attach_media)
      const existing = prevDefinition?.nodes?.[elId] || {};
      const existingConfig = existing.config || {};
      nodesMap[elId] = {
        ...existing,
        id: elId,
        type: elNodeType,
        next: nextElId,
        config: {
          ...existingConfig,
          ...elNodeConfig,
          // Mescla mídia: UI (cfg.media) tem prioridade sobre o que estava salvo
          ...(elNodeConfig.media !== undefined
            ? { media: { ...(existingConfig.media || {}), ...elNodeConfig.media } }
            : {}),
        },
      };
    });

    // Composto aponta para o primeiro elemento; edge resolver já calculou o destino final
    if (elements.length > 0 && elements[0].elId) {
      nodesMap[composedId].next = elements[0].elId;
    }
  });

  def.nodes = nodesMap;
  def.id = def.id || DEFAULT_FLOW_ID;

  return def;
}


export default function Flows() {
  const { botId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialFlowSlug = new URLSearchParams(location.search).get('flowSlug') || DEFAULT_FLOW_ID;
  const { showError, showSuccess } = useNotification();

  // Mantém o bot deste fluxo como "último selecionado" (UX: o seletor de bot
  // — sidebar e BotSelector — continua refletindo o bot em edição).
  useEffect(() => {
    if (!botId) return;
    try {
      localStorage.setItem('selectedBotId', String(botId));
    } catch { }
  }, [botId]);

  const [loading, setLoading] = useState(true);
  // Fase de "boot" pós-load: overlay do loader cobre o canvas enquanto o
  // ReactFlow monta e centraliza, depois some com fade (visible → hiding → done)
  const [bootPhase, setBootPhase] = useState('visible');
  const [saving, setSaving] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const [flowRecord, setFlowRecord] = useState(null);
  const [rfNodes, setRfNodes] = useState([]);
  const [rfEdges, setRfEdges] = useState([]);
  const [isFlowEnabled, setIsFlowEnabled] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  // Bot dono do fluxo (para exibir o BotChip + nome antes do nome do fluxo).
  // Derivado da lista de bots do usuário autenticado (não existe endpoint bots.get).
  const bot = useMemo(() => {
    const bots = getVisibleBots(user?.bots);
    return bots.find((b) => String(b.id) === String(botId)) || null;
  }, [user, botId]);
  const [editingName, setEditingName] = useState(false);
  const botList = useMemo(() => (bot ? [bot] : []), [bot]);
  const photoUrls = useBotPhotos(botList);

  // Busca de texto dentro do fluxo
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(0);

  // Navegação nó anterior / próximo (barra inferior)
  const [focusedNodeId, setFocusedNodeId] = useState(null);

  // Estatísticas de alcance por nó (modo Stats)
  const [statsEnabled, setStatsEnabled] = useState(false);
  const [nodeStats, setNodeStats] = useState({});
  const [statsStarts, setStatsStarts] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);
  const statsCtxValue = useMemo(
    () => ({ enabled: statsEnabled, stats: nodeStats, starts: statsStarts }),
    [statsEnabled, nodeStats, statsStarts],
  );

  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const handleFlowInit = useCallback((instance) => {
    setReactFlowInstance(instance);
    // Injeta uma seta pequena e discreta (cor principal) na ponta das conexões
    const svg = reactFlowWrapper.current?.querySelector('svg');
    if (svg && !svg.querySelector('#flow-purple-arrow')) {
      const ns = 'http://www.w3.org/2000/svg';
      const defs = document.createElementNS(ns, 'defs');
      const marker = document.createElementNS(ns, 'marker');
      marker.setAttribute('id', 'flow-purple-arrow');
      marker.setAttribute('markerWidth', '4');
      marker.setAttribute('markerHeight', '4');
      marker.setAttribute('refX', '3.5');
      marker.setAttribute('refY', '2');
      marker.setAttribute('orient', 'auto');
      const polygon = document.createElementNS(ns, 'polygon');
      polygon.setAttribute('points', '0 0, 4 2, 0 4');
      polygon.setAttribute('fill', '#8b5cf6');
      marker.appendChild(polygon);
      defs.appendChild(marker);
      svg.insertBefore(defs, svg.firstChild);
    }
  }, []);
  const isLoadingRef = useRef(false);
  const flowRecordRef = useRef(null);
  const rfNodesRef = useRef([]);
  const rfEdgesRef = useRef([]);
  // ref para sempre apontar para a versão mais recente de loadFlows sem criar dependência no effect
  const loadFlowsRef = useRef(null);

  // Histórico (desfazer/refazer) e área de transferência (copiar/colar)
  const historyRef = useRef({ past: [], future: [] });
  const copiedNodeRef = useRef(null);
  const [showShortcuts, setShowShortcuts] = useState(false);

  const pushHistory = useCallback(() => {
    const h = historyRef.current;
    h.past.push({ nodes: rfNodesRef.current, edges: rfEdgesRef.current });
    if (h.past.length > 60) h.past.shift();
    h.future = [];
  }, []);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.past.length === 0) return;
    const prev = h.past.pop();
    h.future.push({ nodes: rfNodesRef.current, edges: rfEdgesRef.current });
    setRfNodes(prev.nodes);
    setRfEdges(prev.edges);
  }, []);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.future.length === 0) return;
    const next = h.future.pop();
    h.past.push({ nodes: rfNodesRef.current, edges: rfEdgesRef.current });
    setRfNodes(next.nodes);
    setRfEdges(next.edges);
  }, []);

  const nodeTypes = useMemo(
    () => ({
      flowNode: FlowNodeComponent,
    }),
    [],
  );

  const edgeTypes = useMemo(
    () => ({
      deletable: DeletableEdge,
    }),
    [],
  );

  useEffect(() => {
    flowRecordRef.current = flowRecord;
  }, [flowRecord]);

  useEffect(() => {
    rfNodesRef.current = rfNodes;
  }, [rfNodes]);

  useEffect(() => {
    rfEdgesRef.current = rfEdges;
  }, [rfEdges]);

  const handleNodeTextChange = useCallback((nodeId, newText) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? {
            ...n,
            data: {
              ...n.data,
              text: newText,
            },
          }
          : n,
      ),
    );
  }, []);

  const handleNodeDelayChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? {
            ...n,
            data: {
              ...n.data,
              ...patch,
            },
          }
          : n,
      ),
    );
  }, []);

  const handleNodeTypingChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? {
            ...n,
            data: {
              ...n.data,
              ...patch,
            },
          }
          : n,
      ),
    );
  }, []);

  const ensureNodePersisted = useCallback(
    async (nodeId) => {
      if (!botId) return false;

      const currentFlow = flowRecordRef.current;
      if (!currentFlow) return false;

      if (currentFlow.definition?.nodes?.[nodeId]) {
        return true;
      }

      try {
        const definition = flowToDefinition(
          rfNodesRef.current,
          rfEdgesRef.current,
          currentFlow.definition || {},
        );
        const payload = {
          id: currentFlow.id,
          name: currentFlow.name || 'Fluxo /start',
          slug: currentFlow.slug || DEFAULT_FLOW_ID,
          is_active: true,
          definition,
        };

        const { data } = await authService.flows.save(botId, payload);
        if (!data.success) throw new Error(data.error || 'Erro ao salvar fluxo');

        setFlowRecord((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            id: data.flow_id || prev.id,
            definition,
          };
        });

        return true;
      } catch (err) {
        showError(err?.response?.data?.error || err.message);
        return false;
      }
    },
    [botId, showError],
  );

  const handleNodeMediaUpload = useCallback(
    async (nodeId, mediaType, file) => {
      if (!file || !botId) return;
      const currentFlow = flowRecordRef.current;

      if (!currentFlow?.id) {
        showError('Salve o fluxo antes de anexar mídia.');
        return;
      }

      const canAttach = await ensureNodePersisted(nodeId);
      if (!canAttach) return;

      const MAX_FILE_SIZE = 30 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        showError(`❌ Arquivo muito grande: ${fileSizeMB}MB (máximo 30MB)`);
        return;
      }

      setRfNodes((prev) =>
        prev.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, uploadingMedia: true } } : n),
      );

      try {
        const uploadResponse = await authService.uploads.upload_media(botId, file);
        const uploadedFile = uploadResponse.data;

        if (!uploadedFile?.id) {
          throw new Error('Upload inválido');
        }

        // file_id pode estar ausente se o envio ao Telegram ainda está em background.
        // Aguarda até estar disponível antes de fazer o attach.
        let fileId = uploadedFile.file_id;
        let thumbnailFileId = uploadedFile.thumbnail_file_id || null;
        if (!fileId) {
          for (let attempt = 0; attempt < 30; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const pollRes = await authService.uploads.get_upload(uploadedFile.id);
            if (pollRes.data?.file_id) {
              fileId = pollRes.data.file_id;
              thumbnailFileId = pollRes.data.thumbnail_file_id || null;
              break;
            }
          }
          if (!fileId) throw new Error('Timeout aguardando envio para o Telegram. Tente novamente.');
        }

        await authService.uploads.attach_media({
          upload_id: uploadedFile.id,
          target_type: 'flow_node',
          target_id: flowRecordRef.current.id,
          slot: nodeId,
          media_type: mediaType,
        });

        const storedKey = uploadedFile.media_type || mediaType;
        const currentMedia =
          currentFlow.definition?.nodes?.[nodeId]?.config?.media || {};
        const updatedMedia = {
          ...currentMedia,
          [storedKey]: fileId,
          ...(thumbnailFileId ? { thumbnail: thumbnailFileId } : {}),
          fileName: file.name,
        };

        setFlowRecord((prev) => {
          if (!prev) return prev;
          const definition = { ...(prev.definition || { id: DEFAULT_FLOW_ID, nodes: {} }) };
          const nodes = { ...(definition.nodes || {}) };
          const existing = nodes[nodeId];

          if (existing) {
            const config = { ...(existing.config || {}) };
            config.media = updatedMedia;
            existing.config = config;
            nodes[nodeId] = existing;
            definition.nodes = nodes;
          }

          return { ...prev, definition };
        });

        setRfNodes((prev) =>
          prev.map((n) =>
            n.id === nodeId
              ? {
                ...n,
                data: {
                  ...n.data,
                  media: updatedMedia,
                  hasMedia: Boolean(
                    updatedMedia.photo ||
                    updatedMedia.video ||
                    updatedMedia.audio ||
                    updatedMedia.voice,
                  ),
                },
              }
              : n,
          ),
        );

        showSuccess('Mídia salva com sucesso!');
      } catch (err) {
        showError(err?.response?.data?.error || err.message);
      } finally {
        setRfNodes((prev) =>
          prev.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, uploadingMedia: false } } : n),
        );
      }
    },
    [botId, ensureNodePersisted, showError, showSuccess],
  );

  // Upload de mídia para um elemento dentro de um nó composed.
  // Cada elemento é um nó real na definição (com ID = elId). Antes de qualquer
  // attach_media, o fluxo é salvo para garantir que o nó filho exista no backend
  // (independente de o nó composed já estar persistido ou não).
  const handleNodeComposedMediaUpload = useCallback(
    async (nodeId, elId, mediaType, file, elementIndex) => {
      if (!file || !botId) return;
      const currentFlow = flowRecordRef.current;

      if (!currentFlow?.id) {
        showError('Salve o fluxo antes de anexar mídia.');
        return;
      }

      const MAX_FILE_SIZE = 30 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        showError(`❌ Arquivo muito grande: ${(file.size / 1024 / 1024).toFixed(2)}MB (máximo 30MB)`);
        return;
      }

      // Sempre salva o fluxo antes de anexar — garante que o nó filho (elId) exista
      // no backend mesmo que o composed já estivesse salvo antes desse elemento ser adicionado.
      try {
        const definition = flowToDefinition(
          rfNodesRef.current,
          rfEdgesRef.current,
          currentFlow.definition || {},
        );
        const { data: saveData } = await authService.flows.save(botId, {
          id: currentFlow.id,
          name: currentFlow.name || 'Fluxo /start',
          slug: currentFlow.slug || DEFAULT_FLOW_ID,
          is_active: true,
          definition,
        });
        if (!saveData.success) throw new Error(saveData.error || 'Erro ao salvar fluxo');
        setFlowRecord((prev) =>
          prev ? { ...prev, id: saveData.flow_id || prev.id, definition } : prev,
        );
      } catch (saveErr) {
        showError(saveErr?.response?.data?.error || saveErr.message);
        return;
      }

      setRfNodes((prev) =>
        prev.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, uploadingElementId: elId } } : n),
      );

      try {
        const uploadResponse = await authService.uploads.upload_media(botId, file);
        const uploadedFile = uploadResponse.data;
        if (!uploadedFile?.id) throw new Error('Upload inválido');

        let fileId = uploadedFile.file_id;
        let thumbnailFileId = uploadedFile.thumbnail_file_id || null;
        if (!fileId) {
          for (let attempt = 0; attempt < 30; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const pollRes = await authService.uploads.get_upload(uploadedFile.id);
            if (pollRes.data?.file_id) {
              fileId = pollRes.data.file_id;
              thumbnailFileId = pollRes.data.thumbnail_file_id || null;
              break;
            }
          }
          if (!fileId) throw new Error('Timeout aguardando envio para o Telegram. Tente novamente.');
        }

        // slot = elId — o nó filho foi criado na etapa de save acima
        await authService.uploads.attach_media({
          upload_id: uploadedFile.id,
          target_type: 'flow_node',
          target_id: flowRecordRef.current.id,
          slot: elId,
          media_type: mediaType,
        });

        const storedKey = uploadedFile.media_type || mediaType;
        const mediaPatch = { [storedKey]: fileId, ...(thumbnailFileId ? { thumbnail: thumbnailFileId } : {}), fileName: file.name };

        // Atualiza flowRecord com a mídia no nó filho
        setFlowRecord((prev) => {
          if (!prev) return prev;
          const definition = { ...(prev.definition || { id: DEFAULT_FLOW_ID, nodes: {} }) };
          const nodesMap = { ...(definition.nodes || {}) };
          const elNode = nodesMap[elId];
          if (elNode) {
            nodesMap[elId] = { ...elNode, config: { ...(elNode.config || {}), media: { ...(elNode.config?.media || {}), ...mediaPatch } } };
            definition.nodes = nodesMap;
          }
          return { ...prev, definition };
        });

        // Atualiza o elemento dentro do nó composed no canvas
        setRfNodes((prev) =>
          prev.map((n) => {
            if (n.id !== nodeId) return n;
            const elems = (n.data?.composedElements || []).map((el, i) => {
              if (el.elId !== elId && i !== elementIndex) return el;
              return { ...el, config: { ...(el.config || {}), media: { ...(el.config?.media || {}), ...mediaPatch } } };
            });
            return { ...n, data: { ...n.data, composedElements: elems } };
          }),
        );

        showSuccess('Mídia salva com sucesso!');
      } catch (err) {
        showError(err?.response?.data?.error || err.message);
      } finally {
        setRfNodes((prev) =>
          prev.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, uploadingElementId: null } } : n),
        );
      }
    },
    [botId, showError, showSuccess],
  );

  const handleNodeMediaChange = useCallback((nodeId, mediaPatch) => {
    const updater = (prev) => {
      if (!prev) return prev;
      const definition = { ...(prev.definition || { id: DEFAULT_FLOW_ID, nodes: {} }) };
      const nodes = { ...(definition.nodes || {}) };
      const existing = nodes[nodeId];
      if (existing) {
        const config = { ...(existing.config || {}) };
        config.media = { ...(config.media || {}), ...mediaPatch };
        nodes[nodeId] = { ...existing, config };
        definition.nodes = nodes;
      }
      return { ...prev, definition };
    };
    setFlowRecord(updater);
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, media: { ...(n.data?.media || {}), ...mediaPatch } } }
          : n,
      ),
    );
  }, []);

  const handleNodeMediaDelete = useCallback(async (nodeId, mediaType) => {
    const currentFlow = flowRecordRef.current;
    if (currentFlow?.id) {
      try {
        await authService.uploads.detach_media('flow_node', currentFlow.id, nodeId, mediaType);
      } catch (_) { /* best effort */ }
    }
    setFlowRecord((prev) => {
      if (!prev) return prev;
      const definition = { ...(prev.definition || { id: DEFAULT_FLOW_ID, nodes: {} }) };
      const nodes = { ...(definition.nodes || {}) };
      const existing = nodes[nodeId];
      if (existing) {
        const config = { ...(existing.config || {}) };
        const media = { ...(config.media || {}) };
        delete media[mediaType];
        config.media = media;
        nodes[nodeId] = { ...existing, config };
        definition.nodes = nodes;
      }
      return { ...prev, definition };
    });
    setRfNodes((prev) =>
      prev.map((n) => {
        if (n.id !== nodeId) return n;
        const media = { ...(n.data?.media || {}) };
        delete media[mediaType];
        return {
          ...n,
          data: {
            ...n.data,
            media,
            hasMedia: Boolean(media.photo || media.video || media.audio || media.voice),
          },
        };
      }),
    );
  }, []);

  const handleNodeSmartDelayChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? {
            ...n,
            data: {
              ...n.data,
              ...patch,
            },
          }
          : n,
      ),
    );
  }, []);

  const handleNodePaymentChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, ...patch } }
          : n,
      ),
    );
  }, []);

  const handleNodeInputChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, ...patch } }
          : n,
      ),
    );
  }, []);

  const handleNodeRandomizerChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, ...patch } }
          : n,
      ),
    );
  }, []);

  const handleNodeTriggerChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, ...patch } }
          : n,
      ),
    );
  }, []);

  const handleNodeEntregavelChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, ...patch } }
          : n,
      ),
    );
  }, []);

  const handleNodeButtonsChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, ...patch } }
          : n,
      ),
    );
  }, []);

  const handleNodeComposedChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, ...patch } }
          : n,
      ),
    );
  }, []);

  const handleNodeGotoChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, ...patch } }
          : n,
      ),
    );
  }, []);

  const handleNodeOrderBumpChange = useCallback((nodeId, patch) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, ...patch } }
          : n,
      ),
    );
  }, []);

  const handleOrderBumpMediaUpload = useCallback(
    async (nodeId, file) => {
      if (!file || !botId) return;
      const currentFlow = flowRecordRef.current;

      if (!currentFlow?.id) {
        showError('Salve o fluxo antes de anexar mídia.');
        return;
      }

      const currentNode = rfNodesRef.current.find((n) => n.id === nodeId);
      if ((currentNode?.data?.orderBumpMedia || []).length >= 5) {
        showError('Máximo de 5 mídias por order bump.');
        return;
      }

      const MAX_FILE_SIZE = 30 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        showError(`❌ Arquivo muito grande: ${(file.size / 1024 / 1024).toFixed(2)}MB (máximo 30MB)`);
        return;
      }

      const canAttach = await ensureNodePersisted(nodeId);
      if (!canAttach) return;

      setRfNodes((prev) =>
        prev.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, uploadingOrderBumpMedia: true } } : n),
      );

      try {
        const uploadResponse = await authService.uploads.upload_media(botId, file);
        const uploadedFile = uploadResponse.data;
        if (!uploadedFile?.id) throw new Error('Upload inválido');

        let fileId = uploadedFile.file_id;
        if (!fileId) {
          for (let attempt = 0; attempt < 30; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const pollRes = await authService.uploads.get_upload(uploadedFile.id);
            if (pollRes.data?.file_id) { fileId = pollRes.data.file_id; break; }
          }
          if (!fileId) throw new Error('Timeout aguardando envio para o Telegram. Tente novamente.');
        }

        const mediaId = `ob_${Date.now()}`;

        const newItem = {
          id: mediaId,
          type: uploadedFile.media_type || 'image',
          fileId,
          fileName: file.name,
          spoiler: false,
        };

        setRfNodes((prev) =>
          prev.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, orderBumpMedia: [...(n.data?.orderBumpMedia || []), newItem] } }
              : n,
          ),
        );

        showSuccess('Mídia salva com sucesso!');
      } catch (err) {
        showError(err?.response?.data?.error || err.message);
      } finally {
        setRfNodes((prev) =>
          prev.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, uploadingOrderBumpMedia: false } } : n),
        );
      }
    },
    [botId, ensureNodePersisted, showError, showSuccess],
  );

  const handleOrderBumpMediaRemove = useCallback((nodeId, mediaId) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, orderBumpMedia: (n.data?.orderBumpMedia || []).filter((m) => m.id !== mediaId) } }
          : n,
      ),
    );
  }, []);

  const handleOrderBumpMediaToggleSpoiler = useCallback((nodeId, mediaId) => {
    setRfNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? {
            ...n,
            data: {
              ...n.data,
              orderBumpMedia: (n.data?.orderBumpMedia || []).map((m) =>
                m.id === mediaId ? { ...m, spoiler: !m.spoiler } : m,
              ),
            },
          }
          : n,
      ),
    );
  }, []);

  const handleDeleteNode = useCallback((nodeId) => {
    pushHistory();
    setRfNodes((prev) => prev.filter((n) => n.id !== nodeId));
    setRfEdges((prev) => prev.filter((e) => e.source !== nodeId && e.target !== nodeId));
  }, [pushHistory]);

  const handleDeleteEdge = useCallback((edgeId) => {
    pushHistory();
    setRfEdges((prev) => prev.filter((e) => e.id !== edgeId));
  }, [pushHistory]);

  const handleDuplicateNode = useCallback((nodeId, opts) => {
    const selectNew = !!(opts && opts.select);
    pushHistory();
    setRfNodes((prev) => {
      const original = prev.find((n) => n.id === nodeId);
      if (!original || original.data?.isStart) return prev;

      const newId = `node_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const d = original.data || {};

      const buttonItems = d.buttonItems?.map((btn) => ({
        ...btn,
        id: `btn_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
      }));

      const randomizerOutputs = d.randomizerOutputs?.map((o, i) => ({
        ...o,
        id: `r${i}_${Date.now()}`,
        next: null,
      }));

      const composedElements = d.composedElements?.map((el) => ({
        ...el,
        elId: `e_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        config: {
          ...(el.config || {}),
          media: { ...(el.config?.media || {}) },
        },
      }));

      return [
        ...prev.map((n) => n.id === nodeId ? { ...n, selected: false } : n),
        {
          ...original,
          id: newId,
          selected: selectNew,
          position: {
            x: (original.position?.x ?? 0) + 45,
            y: (original.position?.y ?? 0) + 45,
          },
          data: {
            ...d,
            id: newId,
            media: { ...(d.media || {}) },
            buttonItems,
            randomizerOutputs,
            composedElements,
            onChangeText: handleNodeTextChange,
            onChangeDelay: handleNodeDelayChange,
            onChangeTyping: handleNodeTypingChange,
            onChangeSmartDelay: handleNodeSmartDelayChange,
            onChangePayment: handleNodePaymentChange,
            onChangeInput: handleNodeInputChange,
            onChangeRandomizer: handleNodeRandomizerChange,
            onChangeTrigger: handleNodeTriggerChange,
            onChangeEntregavel: handleNodeEntregavelChange,
            onChangeButtons: handleNodeButtonsChange,
            onChangeComposed: handleNodeComposedChange,
            onChangeGoto: handleNodeGotoChange,
            onChangeOrderBump: handleNodeOrderBumpChange,
            getNodes: () => rfNodesRef.current,
            botId,
            onUploadMedia: handleNodeMediaUpload,
            onUploadComposedMedia: handleNodeComposedMediaUpload,
            onChangeMedia: handleNodeMediaChange,
            onDeleteMedia: handleNodeMediaDelete,
            onOrderBumpMediaUpload: handleOrderBumpMediaUpload,
            onOrderBumpMediaRemove: handleOrderBumpMediaRemove,
            onOrderBumpMediaToggleSpoiler: handleOrderBumpMediaToggleSpoiler,
            onDelete: handleDeleteNode,
            onDuplicate: handleDuplicateNode,
          },
        }];
    });
  }, [
    botId,
    handleNodeTextChange, handleNodeDelayChange, handleNodeTypingChange,
    handleNodeSmartDelayChange, handleNodePaymentChange, handleNodeInputChange,
    handleNodeRandomizerChange, handleNodeTriggerChange, handleNodeEntregavelChange,
    handleNodeButtonsChange, handleNodeComposedChange, handleNodeGotoChange,
    handleNodeOrderBumpChange,
    handleNodeMediaUpload, handleNodeComposedMediaUpload, handleNodeMediaChange,
    handleNodeMediaDelete,
    handleOrderBumpMediaUpload, handleOrderBumpMediaRemove, handleOrderBumpMediaToggleSpoiler,
    handleDeleteNode, pushHistory,
  ]);

  // Copiar / colar / duplicar via teclado — suporta MÚLTIPLOS nós selecionados
  const copySelected = useCallback(() => {
    const ids = rfNodesRef.current.filter((n) => n.selected && !n.data?.isStart).map((n) => n.id);
    if (ids.length) copiedNodeRef.current = ids;
  }, []);

  const pasteCopied = useCallback(() => {
    const ids = copiedNodeRef.current;
    if (!Array.isArray(ids) || ids.length === 0) return;
    // Deseleciona tudo antes; os nós colados ficam selecionados (para mover juntos)
    setRfNodes((prev) => prev.map((n) => ({ ...n, selected: false })));
    ids.forEach((id) => {
      if (rfNodesRef.current.some((n) => n.id === id)) handleDuplicateNode(id, { select: true });
    });
  }, [handleDuplicateNode]);

  const duplicateSelected = useCallback(() => {
    const sel = rfNodesRef.current.filter((n) => n.selected && !n.data?.isStart);
    if (!sel.length) return;
    setRfNodes((prev) => prev.map((n) => ({ ...n, selected: false })));
    sel.forEach((n) => handleDuplicateNode(n.id, { select: true }));
  }, [handleDuplicateNode]);

  const handleToggleFlowEnabled = async () => {
    if (!botId || toggleLoading) return;

    const newValue = !isFlowEnabled;
    try {
      setToggleLoading(true);
      const { data } = await authService.bots.toggle_flow(botId, newValue);
      if (data.success) {
        setIsFlowEnabled(newValue);
        showSuccess(newValue ? 'Flow Engine ativado!' : 'Flow Engine desativado!');
      } else {
        showError(data.error || 'Erro ao atualizar');
      }
    } catch (error) {
      showError('Erro ao atualizar Flow Engine');
    } finally {
      setToggleLoading(false);
    }
  };

  const loadFlows = useCallback(async () => {
    if (!botId || isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const { data } = await authService.flows.list(botId);
      if (!data.success) throw new Error(data.error || 'Erro ao carregar fluxos');

      const startFlow =
        data?.flows?.find((f) => f.slug === initialFlowSlug) ||
        data?.flows?.find((f) => f.slug === DEFAULT_FLOW_ID) ||
        data?.flows?.[0] || null;

      if (startFlow) {
        setFlowRecord(startFlow);
        const { nodes, edges } = definitionToFlow(startFlow.definition || {});
        const nodesWithHandlers = nodes.map((n) => ({
          ...n,
          data: {
            ...(n.data || {}),
            onChangeText: handleNodeTextChange,
            onChangeDelay: handleNodeDelayChange,
            onChangeTyping: handleNodeTypingChange,
            onChangeSmartDelay: handleNodeSmartDelayChange,
            onChangePayment: handleNodePaymentChange,
            onChangeInput: handleNodeInputChange,
            onChangeRandomizer: handleNodeRandomizerChange,
            onChangeTrigger: handleNodeTriggerChange,
            onChangeEntregavel: handleNodeEntregavelChange,
            onChangeButtons: handleNodeButtonsChange,
            onChangeComposed: handleNodeComposedChange,
            onChangeGoto: handleNodeGotoChange,
            onChangeOrderBump: handleNodeOrderBumpChange,
            getNodes: () => rfNodesRef.current,
            botId,
            onUploadMedia: handleNodeMediaUpload,
            onUploadComposedMedia: handleNodeComposedMediaUpload,
            onChangeMedia: handleNodeMediaChange,
            onDeleteMedia: handleNodeMediaDelete,
            onOrderBumpMediaUpload: handleOrderBumpMediaUpload,
            onOrderBumpMediaRemove: handleOrderBumpMediaRemove,
            onOrderBumpMediaToggleSpoiler: handleOrderBumpMediaToggleSpoiler,
            onDelete: handleDeleteNode,
            onDuplicate: handleDuplicateNode,
          },
        }));
        const edgesWithHandlers = edges.map((e) => ({
          ...e,
          type: 'deletable',
          data: {
            ...(e.data || {}),
            onDelete: handleDeleteEdge,
          },
        }));
        setRfNodes(nodesWithHandlers);
        setRfEdges(edgesWithHandlers);
      } else {
        const emptyDef = {
          id: DEFAULT_FLOW_ID,
          start_node: null,
          nodes: {},
        };
        setFlowRecord({
          id: null,
          bot_id: Number(botId),
          name: 'Fluxo /start',
          slug: DEFAULT_FLOW_ID,
          is_active: false,
          definition: emptyDef,
        });

        setRfNodes([
          {
            id: 'start',
            type: 'flowNode',
            data: {
              id: 'start',
              nodeType: 'start',
              text: 'Quando o usuário inicia',
              isStart: true,
              hasMedia: false,
              delayValue: '',
              delayUnit: 'seconds',
              showTyping: false,
              smartDelayMin: '',
              smartDelayMax: '',
              smartDelayUnit: 'seconds',
              typingValue: '',
              typingUnit: 'seconds',
              typingAction: 'typing',
              onChangeText: handleNodeTextChange,
              onChangeDelay: handleNodeDelayChange,
              onChangeTyping: handleNodeTypingChange,
              onChangeSmartDelay: handleNodeSmartDelayChange,
              onChangePayment: handleNodePaymentChange,
              onChangeInput: handleNodeInputChange,
              onChangeRandomizer: handleNodeRandomizerChange,
              onChangeTrigger: handleNodeTriggerChange,
              onChangeEntregavel: handleNodeEntregavelChange,
              onChangeButtons: handleNodeButtonsChange,
              onChangeComposed: handleNodeComposedChange,
              onChangeGoto: handleNodeGotoChange,
              onChangeOrderBump: handleNodeOrderBumpChange,
              getNodes: () => rfNodesRef.current,
              botId,
              onUploadMedia: handleNodeMediaUpload,
              onChangeMedia: handleNodeMediaChange,
              onDeleteMedia: handleNodeMediaDelete,
              onOrderBumpMediaUpload: handleOrderBumpMediaUpload,
              onOrderBumpMediaRemove: handleOrderBumpMediaRemove,
              onOrderBumpMediaToggleSpoiler: handleOrderBumpMediaToggleSpoiler,
              onDelete: handleDeleteNode,
              onDuplicate: handleDuplicateNode,
            },
            position: { x: 0, y: 0 },
          },
        ]);
        setRfEdges([]);
      }
    } catch (err) {
      showError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [
    botId,
    showError,
    handleNodeTextChange,
    handleNodeDelayChange,
    handleNodeTypingChange,
    handleNodeSmartDelayChange,
    handleNodePaymentChange,
    handleNodeInputChange,
    handleNodeRandomizerChange,
    handleNodeTriggerChange,
    handleNodeEntregavelChange,
    handleNodeButtonsChange,
    handleNodeComposedChange,
    handleNodeGotoChange,
    handleNodeOrderBumpChange,
    handleNodeMediaUpload,
    handleNodeComposedMediaUpload,
    handleNodeMediaChange,
    handleNodeMediaDelete,
    handleOrderBumpMediaUpload,
    handleOrderBumpMediaRemove,
    handleOrderBumpMediaToggleSpoiler,
    handleDeleteNode,
    handleDuplicateNode,
    handleDeleteEdge,
  ]);

  // Mantém a ref sempre atualizada com a versão mais recente de loadFlows
  loadFlowsRef.current = loadFlows;

  // Só re-carrega o flow quando o botId muda (mount + troca de bot).
  // NÃO depende de loadFlows diretamente para evitar que re-criações dos
  // handlers (causadas por setFlowRecord/setRfNodes) disparem um reload completo.
  useEffect(() => {
    loadFlowsRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  // Quando o load termina, mantém o overlay do loader por um instante
  // (cobrindo a montagem/fitView do canvas) e então faz o fade-out.
  useEffect(() => {
    if (loading) {
      setBootPhase('visible');
      return undefined;
    }
    const startHide = setTimeout(() => setBootPhase('hiding'), 350);
    const finish = setTimeout(() => setBootPhase('done'), 850);
    return () => {
      clearTimeout(startHide);
      clearTimeout(finish);
    };
  }, [loading]);

  const onNodesChange = useCallback(
    (changes) => {
      if (changes.some((c) => c.type === 'remove')) pushHistory();
      setRfNodes((nds) => applyNodeChanges(changes, nds));
    },
    [pushHistory],
  );

  const onEdgesChange = useCallback(
    (changes) => {
      if (changes.some((c) => c.type === 'remove')) pushHistory();
      setRfEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [pushHistory],
  );

  const onConnect = useCallback((connection) => {
    pushHistory();
    setRfEdges((eds) => [
      ...eds,
      {
        id: `e-${connection.source}-${connection.target}`,
        ...connection,
        type: 'deletable',
        data: {
          onDelete: handleDeleteEdge,
        },
      },
    ]);
  }, [handleDeleteEdge]);

  const onDragStartPalette = (event, type) => {
    event.dataTransfer.setData('application/reactflow', type);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Cria um nó de um tipo do palette numa posição do canvas.
  // Usado tanto pelo drag & drop (desktop) quanto pelo clique (mobile).
  const createNodeAt = useCallback(
    (type, position) => {
      pushHistory();
      setRfNodes((nds) => {
        if (type === 'start') {
          const hasStart = nds.some((n) => n.data?.nodeType === 'start');
          if (hasStart) {
            return nds;
          }
        }

        const id = type === 'start' ? 'start' : `node_${Date.now()}`;
        const isDelay = type === 'delay';
        const isTyping = type === 'typing';
        const isSmartDelay = type === 'smart_delay';
        const isInput = type === 'input';
        const isRandomizer = type === 'randomizer';

        const newNode = {
          id,
          type: 'flowNode',
          position,
          data: {
            id,
            nodeType: type,
            text: type === 'start' ? 'Quando o usuário inicia' : '',
            isStart: type === 'start',
            hasMedia: false,
            delayValue: isDelay ? 5 : '',
            delayUnit: 'seconds',
            showTyping: false,
            smartDelayMin: isSmartDelay ? 5 : '',
            smartDelayMax: isSmartDelay ? 10 : '',
            smartDelayUnit: 'seconds',
            typingValue: isTyping ? 5 : '',
            typingUnit: 'seconds',
            typingAction: 'typing',
            inputPrompt: '',
            inputVar: '',
            inputType: 'text',
            inputTimeout: 60,
            randomizerOutputs: isRandomizer
              ? [{ id: 'r0', percent: 50 }, { id: 'r1', percent: 50 }]
              : undefined,
            triggerType: 'message',
            triggerTimeoutValue: 60,
            triggerTimeoutUnit: 'seconds',
            entregavelDeliveryType: 'vip_link',
            entregavelText: '',
            entregavelUrl: '',
            entregavelButtonText: '',
            entregavelVipGroupId: '',
            // payment node defaults
            showVerifyButton: true,
            showCopyButton: true,
            showQrButton: true,
            showPaymentInstructions: true,
            paymentPlanDuration: 'vitalicio',
            // order_bump node defaults
            orderBumpName: '',
            orderBumpPrice: '',

            orderBumpMessage: '',
            orderBumpMedia: [],
            orderBumpGrouped: false,
            orderBumpHideDecline: false,
            orderBumpDisappearOnClick: false,
            orderBumpAcceptColor: '',
            orderBumpDeclineColor: '',
            orderBumpAcceptLabel: '',
            orderBumpDeclineLabel: '',
            buttonItems: type === 'buttons' ? [
              { id: 'btn_0', text: 'Botão 1', color: 'default' },
              { id: 'btn_1', text: 'Botão 2', color: 'default' },
            ] : undefined,
            buttonDeleteAfterClick: false,
            composedElements: type === 'composed' ? [] : undefined,
            gotoTargetId: '',
            onChangeText: handleNodeTextChange,
            onChangeDelay: handleNodeDelayChange,
            onChangeTyping: handleNodeTypingChange,
            onChangeSmartDelay: handleNodeSmartDelayChange,
            onChangePayment: handleNodePaymentChange,
            onChangeInput: handleNodeInputChange,
            onChangeRandomizer: handleNodeRandomizerChange,
            onChangeTrigger: handleNodeTriggerChange,
            onChangeEntregavel: handleNodeEntregavelChange,
            onChangeButtons: handleNodeButtonsChange,
            onChangeComposed: handleNodeComposedChange,
            onChangeGoto: handleNodeGotoChange,
            onChangeOrderBump: handleNodeOrderBumpChange,
            botId,
            onUploadMedia: handleNodeMediaUpload,
            onUploadComposedMedia: handleNodeComposedMediaUpload,
            onChangeMedia: handleNodeMediaChange,
            onDeleteMedia: handleNodeMediaDelete,
            onOrderBumpMediaUpload: handleOrderBumpMediaUpload,
            onOrderBumpMediaRemove: handleOrderBumpMediaRemove,
            onOrderBumpMediaToggleSpoiler: handleOrderBumpMediaToggleSpoiler,
            onDelete: handleDeleteNode,
            onDuplicate: handleDuplicateNode,
            getNodes: () => rfNodesRef.current,
          },
        };

        return nds.concat(newNode);
      });
    },
    [
      pushHistory,
      handleNodeTextChange,
      handleNodeDelayChange,
      handleNodeTypingChange,
      handleNodeSmartDelayChange,
      handleNodePaymentChange,
      handleNodeInputChange,
      handleNodeRandomizerChange,
      handleNodeTriggerChange,
      handleNodeEntregavelChange,
      handleNodeButtonsChange,
      handleNodeComposedChange,
      handleNodeGotoChange,
      handleNodeOrderBumpChange,
      handleNodeMediaUpload,
      handleNodeComposedMediaUpload,
      handleNodeMediaChange,
      handleNodeMediaDelete,
      handleOrderBumpMediaUpload,
      handleOrderBumpMediaRemove,
      handleOrderBumpMediaToggleSpoiler,
      handleDeleteNode,
      handleDuplicateNode,
    ],
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      if (!type || !reactFlowInstance || !reactFlowWrapper.current) return;

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      createNodeAt(type, position);
    },
    [reactFlowInstance, createNodeAt],
  );

  // Adiciona um bloco por clique (palette mobile): empilha abaixo do nó mais baixo
  const handleAddBlock = useCallback(
    (type) => {
      const nodes = rfNodesRef.current;
      let x = 60;
      let y = 60;
      if (nodes.length > 0) {
        const lowest = nodes.reduce(
          (a, b) => (((b.position?.y ?? 0) > (a.position?.y ?? 0)) ? b : a),
        );
        x = lowest.position?.x ?? 60;
        y = (lowest.position?.y ?? 0) + (lowest.height || 140) + 60;
      }
      createNodeAt(type, { x, y });
      // Centraliza no nó recém-criado para dar feedback visual
      reactFlowInstance?.setCenter?.(x + 110, y + 70, { zoom: 0.9, duration: 350 });
    },
    [createNodeAt, reactFlowInstance],
  );

  const handleAddNode = () => {
    const newId = `node_${Date.now()}`;
    const newNode = {
      id: newId,
      type: 'flowNode',
      data: {
        id: newId,
        nodeType: 'text',
        text: '',
        isStart: false,
        hasMedia: false,
        delayValue: '',
        delayUnit: 'seconds',
        showTyping: false,
        smartDelayMin: '',
        smartDelayMax: '',
        smartDelayUnit: 'seconds',
        typingValue: '',
        typingUnit: 'seconds',
        typingAction: 'typing',
        entregavelDeliveryType: 'text',
        entregavelText: '',
        entregavelUrl: '',
        entregavelButtonText: '',
        entregavelVipGroupId: '',
        buttonItems: undefined,
        buttonDeleteAfterClick: false,
        composedElements: undefined,
        gotoTargetId: '',
        onChangeText: handleNodeTextChange,
        onChangeDelay: handleNodeDelayChange,
        onChangeTyping: handleNodeTypingChange,
        onChangeSmartDelay: handleNodeSmartDelayChange,
        onChangePayment: handleNodePaymentChange,
        onChangeEntregavel: handleNodeEntregavelChange,
        onChangeButtons: handleNodeButtonsChange,
        onChangeComposed: handleNodeComposedChange,
        onChangeGoto: handleNodeGotoChange,
        onUploadMedia: handleNodeMediaUpload,
        onChangeMedia: handleNodeMediaChange,
        onDeleteMedia: handleNodeMediaDelete,
        onDelete: handleDeleteNode,
        onDuplicate: handleDuplicateNode,
        getNodes: () => rfNodesRef.current,
      },
      position: { x: Math.random() * 400, y: Math.random() * 200 },
    };
    setRfNodes((prev) => [...prev, newNode]);
  };

  // IDs dos nós que casam com a busca atual
  const searchMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return rfNodes.filter((n) => nodeSearchText(n).includes(q)).map((n) => n.id);
  }, [searchQuery, rfNodes]);

  const focusMatch = useCallback((idx) => {
    if (!reactFlowInstance || searchMatches.length === 0) return;
    const len = searchMatches.length;
    const nodeId = searchMatches[((idx % len) + len) % len];
    const node = rfNodes.find((n) => n.id === nodeId);
    if (!node) return;
    const x = node.position.x + (node.width || 220) / 2;
    const y = node.position.y + (node.height || 90) / 2;
    reactFlowInstance.setCenter(x, y, { zoom: 1.1, duration: 400 });
  }, [reactFlowInstance, searchMatches, rfNodes]);

  // Ao mudar a QUERY da busca, foca o primeiro resultado.
  // Importante: não depender de searchMatches — ele é recriado a cada edição
  // dos nós (ex: digitando num textarea), o que fazia o canvas recentralizar
  // e "scrollar" sozinho enquanto o usuário escrevia com a busca aberta.
  useEffect(() => {
    if (searchQuery.trim() && searchMatches.length > 0) {
      setSearchIndex(0);
      focusMatch(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Atalhos de teclado do editor
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      const editable = tag === 'input' || tag === 'textarea' || t?.isContentEditable;
      const mod = e.ctrlKey || e.metaKey;

      // Desfazer/Refazer — não interfere quando digitando em campos
      if (mod && (e.key === 'z' || e.key === 'Z')) {
        if (editable) return;
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }
      // Buscar
      if (mod && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (editable) return; // não sequestra copiar/colar/duplicar durante digitação
      if (mod && (e.key === 'c' || e.key === 'C')) {
        copySelected();
      } else if (mod && (e.key === 'v' || e.key === 'V')) {
        e.preventDefault();
        pasteCopied();
      } else if (mod && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        duplicateSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, copySelected, pasteCopied, duplicateSelected]);

  const fetchNodeStats = useCallback(async () => {
    if (!botId || !flowRecord?.id) { setNodeStats({}); setStatsStarts(0); return; }
    try {
      setStatsLoading(true);
      const { data } = await authService.flows.nodeStats(botId, flowRecord.id);
      if (data.success) {
        setNodeStats(data.stats || {});
        setStatsStarts(data.starts || 0);
      }
    } catch (e) {
      showError('Erro ao carregar estatísticas do fluxo');
    } finally {
      setStatsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId, flowRecord?.id]);

  const toggleStats = () => {
    setStatsEnabled((prev) => {
      const next = !prev;
      if (next) fetchNodeStats();
      return next;
    });
  };

  // Navega (centraliza + seleciona) para um nó específico
  const goToNode = useCallback((id) => {
    if (!id) return;
    const node = rfNodesRef.current.find((n) => n.id === id);
    if (!node) return;
    setFocusedNodeId(id);
    setRfNodes((prev) => prev.map((n) => ({ ...n, selected: n.id === id })));
    if (reactFlowInstance) {
      const x = node.position.x + (node.width || 220) / 2;
      const y = node.position.y + (node.height || 90) / 2;
      reactFlowInstance.setCenter(x, y, { zoom: 1.1, duration: 400 });
    }
  }, [reactFlowInstance]);

  // Moeda-base do fluxo (top bar). Padrão BRL; pode virar USD. Guardada na própria
  // definition (carregada via flowToDefinition), então segue o fluxo todo.
  const currencyMode = flowRecord?.definition?.currency_mode === 'usd' ? 'usd' : 'brl';

  // Injeta a moeda-base do fluxo em cada nó (view-only, não persiste no state)
  // para que os nós — ex.: OrderBumpNode — rotulem o preço em R$ ou $.
  // Mantém a referência do nó quando nada muda, para não re-renderizar à toa.
  const rfNodesView = useMemo(
    () => rfNodes.map((n) => (
      n.data?.currencyMode === currencyMode ? n : { ...n, data: { ...n.data, currencyMode } }
    )),
    [rfNodes, currencyMode],
  );

  const handleSetCurrencyMode = (mode) => {
    const next = mode === 'usd' ? 'usd' : 'brl';
    setFlowRecord((prev) => (prev ? { ...prev, definition: { ...(prev.definition || {}), currency_mode: next } } : prev));
  };

  const handleSave = async () => {
    if (!botId || !flowRecord) return;

    // ✅ Validar conteúdo proibido nas mensagens dos nós do fluxo
    const fieldsToCheck = [];
    rfNodes.forEach((n, index) => {
      const label = `Bloco ${index + 1} (${n.data?.nodeType || 'nó'})`;
      if (n.data?.text) fieldsToCheck.push({ label, value: n.data.text });
      if (n.data?.entregavelText) fieldsToCheck.push({ label: `${label} - Entregável`, value: n.data.entregavelText });
      if (n.data?.messageBefore) fieldsToCheck.push({ label: `${label} - Mensagem antes do PIX`, value: n.data.messageBefore });
      if (n.data?.messageAfter) fieldsToCheck.push({ label: `${label} - Mensagem depois do PIX`, value: n.data.messageAfter });
      if (n.data?.orderBumpMessage) fieldsToCheck.push({ label: `${label} - Mensagem Order Bump`, value: n.data.orderBumpMessage });
    });
    const contentCheck = validateFields(fieldsToCheck);
    if (!contentCheck.isValid) {
      showError(contentCheck.error);
      return;
    }

    // ✅ Entregável de link externo sem texto de botão: o botão é descartado
    // silenciosamente ao entregar (nenhum botão sem texto é enviado) — bloqueia
    // o save em vez de deixar o entregável quebrado ir pro ar.
    const brokenExternalLink = rfNodes.findIndex((n, index) =>
      n.data?.nodeType === 'entregavel' &&
      n.data?.entregavelDeliveryType === 'external_link' &&
      n.data?.entregavelUrl &&
      !n.data?.entregavelButtonText?.trim()
    );
    if (brokenExternalLink !== -1) {
      showError(`Bloco ${brokenExternalLink + 1}: preencha o texto do botão do entregável de link externo antes de salvar.`);
      return;
    }

    setSaving(true);

    try {
      const definition = flowToDefinition(rfNodes, rfEdges, flowRecord.definition || {});

      const payload = {
        id: flowRecord.id,
        name: flowRecord.name || 'Fluxo /start',
        slug: flowRecord.slug || DEFAULT_FLOW_ID,
        // Não ativa o fluxo ao salvar — preserva o estado atual (ativação é manual na lista)
        is_active: !!flowRecord.is_active,
        definition,
      };

      const { data } = await authService.flows.save(botId, payload);
      if (!data.success) throw new Error(data.error || 'Erro ao salvar fluxo');

      setFlowRecord((prev) => prev ? { ...prev, id: data.flow_id || prev.id, definition } : prev);
      showSuccess('Fluxo salvo com sucesso!');
    } catch (err) {
      const apiError = err?.response?.data?.error || err.message;

      if (apiError && apiError.includes('fluxo com esse slug')) {
        try {
          const { data } = await authService.flows.list(botId);
          if (data.success) {
            const existing =
              data.flows?.find((f) => f.slug === (flowRecord.slug || DEFAULT_FLOW_ID)) || null;

            if (existing) {
              const definition = flowToDefinition(rfNodes, rfEdges, existing.definition || {});

              const payload = {
                id: existing.id,
                name: existing.name || flowRecord.name || 'Fluxo /start',
                slug: existing.slug,
                // Preserva o estado de ativação existente — não ativa ao salvar
                is_active: !!existing.is_active,
                definition,
              };

              const saveExisting = await authService.flows.save(botId, payload);
              if (saveExisting.data?.success) {
                setFlowRecord((prev) => prev ? { ...prev, id: saveExisting.data.flow_id || existing.id, definition } : prev);
                showSuccess('Fluxo salvo com sucesso!');
                return;
              }
            }
          }
        } catch (innerErr) {
          // eslint-disable-next-line no-console
          console.error(innerErr);
        }
      }

      showError(apiError || 'Erro ao salvar fluxo');
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return <FlowLoaderScreen />;
  }

  const containerClass = isFullScreen
    ? 'flows-container-fullscreen flex flex-col md:flex-row'
    : 'flex flex-col md:flex-row';

  // Navegação anterior/próximo — segue as conexões a partir do nó atual
  const navCurrentId =
    rfNodes.find((n) => n.selected)?.id
    || focusedNodeId
    || rfNodes.find((n) => n.data?.isStart)?.id
    || rfNodes[0]?.id
    || null;
  const navNode = navCurrentId ? rfNodes.find((n) => n.id === navCurrentId) : null;
  const navMeta = NODE_META[navNode?.data?.nodeType] || { label: 'Nó', icon: 'fas fa-circle' };
  const navColor = NODE_COLORS[navNode?.data?.nodeType] || '#64748b';
  const navPrevId = navCurrentId ? (rfEdges.find((e) => e.target === navCurrentId)?.source || null) : null;
  const navBranches = navNode
    ? resolveBranches(navNode, rfEdges.filter((e) => e.source === navCurrentId))
    : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>

      {/* Overlay do loader — cobre o canvas até o fluxo estar visível */}
      {bootPhase !== 'done' && (
        <FlowLoaderScreen overlay hiding={bootPhase === 'hiding'} />
      )}

      {/* Navbar completa — largura total, acima da sidebar e do canvas */}
      <div className="flex items-center justify-between gap-2 flex-wrap px-4 py-2 border-b border-white/10 bg-black/40 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap min-w-0">

                <div className="flex items-center gap-2 min-w-0">
                  <button
                    type="button"
                    
                    onClick={() => navigate('/bots/flows')}
                  >
                    <i className="fas fa-arrow-left me-1" />
                  </button>

                  {/* Nome do fluxo — editável inline */}
                  {editingName ? (
                    <input
                      autoFocus
                      className="input3 text-sm font-semibold flex-none"
                      style={{ width: 200, padding: '5px 10px', borderRadius: 8 }}
                      maxLength={50}
                      value={flowRecord?.name || ''}
                      onChange={(e) => setFlowRecord((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
                      onBlur={() => setEditingName(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); setEditingName(false); }
                        if (e.key === 'Escape') setEditingName(false);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-sm font-semibold text-white hover:text-white/80 min-w-0"
                      title="Clique para renomear o fluxo"
                      onClick={() => setEditingName(true)}
                    >
                      <span className="truncate">{flowRecord?.name || 'Fluxo /start'}</span>
                      <i className="fas fa-pen text-[10px] text-gray-400" />
                    </button>
                  )}

                  {/* Mercado/moeda-base do fluxo — mesmo controle do editar bot */}
                  <FlowMarketSettings currencyMode={flowRecord ? currencyMode : null} onChange={handleSetCurrencyMode} />
                </div>

          {/* BotChip(s) do fluxo — 1:1 por enquanto, preparado para múltiplos bots */}
          {botList.map((b) => (
            <BotChip key={b.id} photoUrl={photoUrls[b.id]} label={b.bot_username || b.bot_name || `bot${b.id}`} />
          ))}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
              {/* Toolbar de ícones — agrupada e coesa */}

              {/* 1) Desfazer / Refazer (primeiros) */}
              <button type="button" className="action-btn" title="Desfazer (Ctrl+Z)" onClick={undo}>
                <i className="fas fa-arrow-rotate-left" />
              </button>
              <button type="button" className="action-btn" title="Refazer (Ctrl+Shift+Z)" onClick={redo}>
                <i className="fas fa-arrow-rotate-right" />
              </button>

              <div className="w-px h-6 bg-white/10 mx-1" />

              {/* 2) Busca — ícone que expande em input */}
              {searchOpen ? (
                <div className="flex items-center gap-1 bg-black/40 border border-white/15 rounded-lg px-2 h-8">
                  <i className="fas fa-magnifying-glass text-xs text-gray-400" />
                  <input
                    autoFocus
                    className="bg-transparent border-0 outline-none text-sm text-white w-40 placeholder:text-white/30"
                    placeholder="Buscar texto..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const next = searchIndex + 1;
                        setSearchIndex(next);
                        focusMatch(next);
                      }
                      if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); }
                    }}
                  />
                  {searchQuery.trim() && (
                    <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
                      {searchMatches.length ? `${(((searchIndex % searchMatches.length) + searchMatches.length) % searchMatches.length) + 1}/${searchMatches.length}` : '0/0'}
                    </span>
                  )}
                  <button
                    type="button"
                    className="text-gray-400 hover:text-white px-1"
                    title="Fechar busca"
                    onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                  >
                    <i className="fas fa-xmark text-xs" />
                  </button>
                </div>
              ) : (
                <button type="button" className="action-btn" title="Buscar texto no fluxo (Ctrl+F)" onClick={() => setSearchOpen(true)}>
                  <i className="fas fa-magnifying-glass" />
                </button>
              )}

              {/* 3) Stats + atualizar */}
              <button
                type="button"
                className={`action-btn ${statsEnabled ? 'action-btn--purple' : ''}`}
                title="Mostrar quantas pessoas chegaram a cada nó"
                onClick={toggleStats}
              >
                <i className="fas fa-chart-column" />
              </button>
              {statsEnabled && (
                <button
                  type="button"
                  className="action-btn"
                  title="Atualizar estatísticas"
                  disabled={statsLoading}
                  onClick={fetchNodeStats}
                >
                  <i className={`fas fa-arrows-rotate ${statsLoading ? 'fa-spin' : ''}`} />
                </button>
              )}

              <div className="w-px h-6 bg-white/10 mx-1" />

              {/* 4) Tela cheia + atalhos */}
              <button
                type="button"
                className="action-btn"
                title="Tela cheia"
                onClick={() => {
                  if (!document.fullscreenElement) {
                    document.documentElement.requestFullscreen?.();
                  } else {
                    document.exitFullscreen?.();
                  }
                }}
              >
                <i className="fas fa-expand" />
              </button>
              <div className="relative">
                <button
                  type="button"
                  className="action-btn"
                  title="Atalhos do teclado"
                  onClick={() => setShowShortcuts((v) => !v)}
                >
                  <i className="fas fa-question" />
                </button>
                {showShortcuts && (
                  <div className="absolute right-0 mt-2 z-50 w-64 rounded-lg border border-white/10 bg-[#0f1419] shadow-xl p-3 text-left">
                    <div className="text-sm font-semibold mb-2 text-white">Atalhos do Teclado</div>
                    {[
                      ['Buscar', 'Ctrl + F'],
                      ['Copiar nó', 'Ctrl + C'],
                      ['Colar nó', 'Ctrl + V'],
                      ['Duplicar nó', 'Ctrl + D'],
                      ['Deletar', 'Delete / Backspace'],
                      ['Desfazer', 'Ctrl + Z'],
                      ['Refazer', 'Ctrl + Shift + Z'],
                      ['Selecionar (caixa)', 'Shift / Ctrl + Arrastar'],
                      ['Adicionar à seleção', 'Shift / Ctrl + Clique'],
                    ].map(([label, keys]) => (
                      <div key={label} className="flex items-center justify-between py-1 text-xs">
                        <span className="text-gray-400">{label}</span>
                        <span className="text-[var(--purple,#8b5cf6)] font-medium">{keys}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="w-px h-6  gap-4 bg-white/10 mx-1" />

              {/* 5)  Salvar — par de ações padronizado no fim */}

              <button
                type="button"
                className="btn-primary"
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-1" /> Salvando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-1" /> Salvar
                  </>
                )}
          </button>
        </div>
      </div>

      <div className={containerClass} style={{ flex: 1, minHeight: 0 }}>
        {/* Sidebar de blocos disponíveis */}
        <FlowSidebar onDragStartPalette={onDragStartPalette} onAddBlock={handleAddBlock} />

        {/* Canvas principal */}
        <div
          ref={reactFlowWrapper}
          className="flex-1 min-h-0 border border-white/10 rounded-lg overflow-hidden bg-[#050816]"
        >
          <FlowStatsContext.Provider value={statsCtxValue}>
            <ReactFlow
              nodes={rfNodesView}
              edges={rfEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={handleFlowInit}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              selectionKeyCode={['Shift', 'Control', 'Meta']}
              multiSelectionKeyCode={['Shift', 'Control', 'Meta']}
              selectionMode="partial"
              selectNodesOnDrag={false}
              deleteKeyCode={['Backspace', 'Delete']}
              fitView
            >
              <Background />
              <MiniMap
                nodeColor={(node) => NODE_COLORS[node.data?.nodeType] ?? '#64748b'}
                nodeStrokeWidth={3}
                zoomable
                pannable
                style={{ backgroundColor: '#0f172a' }}
                maskColor="rgba(0, 0, 0, 0.65)"
              />
              <Controls />

              {/* Navegação inferior: nó anterior / próximo */}
              {navNode && (
                <Panel position="bottom-center">
                  <div className="flex items-center gap-2 pb-1">
                    {/* Anterior */}
                    <button
                      type="button"
                      className="fnav-pill"
                      title="Nó anterior"
                      disabled={!navPrevId}
                      onClick={() => goToNode(navPrevId)}
                    >
                      <i className="fas fa-chevron-left text-xs" />
                      <span>Anterior</span>
                    </button>

                    {/* Nó atual */}
                    <div className="fnav-pill fnav-pill--current" title={navNode.data?.name || navMeta.label}>
                      <span
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] text-black"
                        style={{ backgroundColor: navColor }}
                      >
                        {typeof navMeta.icon === 'string' ? <i className={navMeta.icon} /> : navMeta.icon}
                      </span>
                      <span className="truncate max-w-[140px]">{navMeta.label}</span>
                    </div>

                    {/* Próximo(s) / ramos */}
                    {navBranches.length === 0 ? (
                      <button type="button" className="fnav-pill" disabled>
                        <span>Fim</span>
                      </button>
                    ) : (
                      navBranches.map((b, i) => (
                        <button
                          key={`${b.targetId}-${i}`}
                          type="button"
                          className={`fnav-pill ${b.tone === 'green' ? 'fnav-pill--green' : b.tone === 'red' ? 'fnav-pill--red' : ''}`}
                          title={`Ir para: ${b.label}`}
                          onClick={() => goToNode(b.targetId)}
                        >
                          <span className="truncate max-w-[120px]">{b.label}</span>
                          <i className="fas fa-chevron-right text-xs" />
                        </button>
                      ))
                    )}
                  </div>
                </Panel>
              )}
            </ReactFlow>
          </FlowStatsContext.Provider>
        </div>

      </div>
    </div>
  );
}
